import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { budgetService, calculateTotal } from '../services/budgetService';
import { ServiceType, SERVICE_LABELS, SERVICE_CATEGORIES, UserProfile, Budget, Room, BudgetItem, getServiceLabel, getServiceCategory } from '../types';
import { AuthUser } from '../lib/supabase';
import { getLogoBase64 } from '../lib/pdfHelper';
import { formatCurrency, formatBRLNumber, parseBRLNumber, getRoomMetrics, formatArea, formatDim } from '../lib/formatters';

interface CalculatorProps {
  user: AuthUser;
  profile: UserProfile | null;
}

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

const Calculator = ({ user, profile }: CalculatorProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [notes, setNotes] = useState('');

  // Estados de múltiplos cômodos
  const [rooms, setRooms] = useState<Room[]>([
    { id: generateId(), name: 'Cômodo 1', width: '', depth: '', height: '', selected_services: [] }
  ]);
  const [activeRoomId, setActiveRoomId] = useState<string>(rooms[0].id);

  // Resultado do cálculo
  const [calculationResult, setCalculationResult] = useState<{
    rooms: { room_id: string; items: any[]; total: number }[];
    grand_total: number;
  } | null>(null);

  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const activeRoom = rooms.find((r: Room) => r.id === activeRoomId) || rooms[0];

  useEffect(() => {
    if (location.state && location.state.budget) {
      const b = location.state.budget as Budget;
      setEditingId(b.id);
      setClientName(b.client_name);
      setClientPhone(b.client_phone || '');
      setNotes(b.notes || '');

      if (b.rooms && b.rooms.length > 0) {
        setRooms(b.rooms);
        setActiveRoomId(b.rooms[0].id);

        // Recalcular visualmente se já tiver itens (opcional, ou apenas setar o resultado se já veio calculado)
        // Aqui vamos reconstruir o calculationResult baseado nos itens salvos
        const resultRooms = b.rooms.map(room => {
          const roomItems = b.items.filter(i => i.room_id === room.id);
          const roomTotal = roomItems.reduce((acc, item) => acc + item.subtotal, 0);
          return { room_id: room.id, items: roomItems, total: roomTotal };
        });
        setCalculationResult({
          rooms: resultRooms,
          grand_total: b.total_value
        });

      } else {
        // Legado: converter campos flat para um cômodo
        const legacyRoom: Room = {
          id: generateId(),
          name: 'Principal',
          width: b.width || 0,
          depth: b.depth || 0,
          height: b.height || 0,
          selected_services: b.selected_services || []
        };
        setRooms([legacyRoom]);
        setActiveRoomId(legacyRoom.id);

        // Itens legados sem room_id
        const roomTotal = b.items.reduce((acc, item) => acc + item.subtotal, 0);
        setCalculationResult({
          rooms: [{ room_id: legacyRoom.id, items: b.items, total: roomTotal }],
          grand_total: b.total_value
        });
      }
    }
  }, [location.state]);

  const updateRoom = (roomId: string, updates: Partial<Room>) => {
    setRooms((prev: Room[]) => prev.map((r: Room) => r.id === roomId ? { ...r, ...updates } : r));
  };

  const addRoom = () => {
    const newRoom: Room = {
      id: generateId(),
      name: `Cômodo ${rooms.length + 1}`,
      width: '', depth: '', height: '',
      selected_services: []
    };
    setRooms((prev: Room[]) => [...prev, newRoom]);
    setActiveRoomId(newRoom.id);
  };

  const removeRoom = (roomId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (rooms.length <= 1) return;
    const newRooms = rooms.filter((r: Room) => r.id !== roomId);
    setRooms(newRooms);
    if (activeRoomId === roomId) {
      setActiveRoomId(newRooms[0].id);
    }
  };

  const toggleService = (service: string) => {
    const currentServices = activeRoom.selected_services;
    const newServices = currentServices.includes(service)
      ? currentServices.filter((s: string) => s !== service)
      : [...currentServices, service];
    updateRoom(activeRoomId, { selected_services: newServices });
  };

  const addManualItem = () => {
    const newItem = { id: generateId(), name: '', value: '' };
    const manual_items = [...(activeRoom.manual_items || []), newItem];
    updateRoom(activeRoomId, { manual_items });
  };

  const removeManualItem = (itemId: string) => {
    const manual_items = activeRoom.manual_items?.filter((i: any) => i.id !== itemId);
    updateRoom(activeRoomId, { manual_items });
  };

  const updateManualItem = (itemId: string, field: string, value: any) => {
    const manual_items = activeRoom.manual_items?.map((i: any) =>
      i.id === itemId ? { ...i, [field]: value } : i
    );
    updateRoom(activeRoomId, { manual_items });
  };

  const handleCalculate = () => {
    if (!profile) {
      alert('Configure seu perfil primeiro com os preços dos serviços.');
      navigate('/perfil');
      return;
    }

    const calculatedRooms = [];
    let grandTotal = 0;

    for (const room of rooms) {
      const w = parseFloat(room.width.toString());
      const d = parseFloat(room.depth.toString());
      const h = parseFloat(room.height.toString()) || 0;

      if (!w && !d) {
        alert(`Informe ao menos uma dimensão (Largura ou Comprimento) para o cômodo: ${room.name}`);
        setActiveRoomId(room.id);
        return;
      }
      if (room.selected_services.length === 0) {
        alert(`Selecione pelo menos um serviço para o cômodo: ${room.name}`);
        setActiveRoomId(room.id);
        return;
      }

      const perimeter = ((w || 0) + (d || 0)) * 2;
      const areaFloor = (w || 0) * (d || 0);
      const areaWall = perimeter * h;

      try {
        const payload = room.selected_services.map((type: string) => {
          const category = getServiceCategory(type, profile);
          let quantity = 0;
          if (category === 'area') quantity = areaFloor;
          else if (category === 'linear') quantity = perimeter;
          else if (category === 'parede') {
            if (!h) {
              alert(`A altura é obrigatória para o serviço ${getServiceLabel(type, profile)} em ${room.name}`);
              throw new Error(`Height missing in ${room.name}`);
            }
            quantity = areaWall;
          } else if (category === 'parede_simples') {
            if (!h) {
              alert(`A altura é obrigatória para o serviço ${getServiceLabel(type, profile)} em ${room.name}`);
              throw new Error(`Height missing in ${room.name}`);
            }
            quantity = (d || w || 0) * h; // Usa d (comprimento) ou w (largura) x Altura (h)
          } else if (category === 'unidade') {
            quantity = 1;
          }

          const price = profile.prices[type] || 0;
          return { type, quantity: parseFloat(quantity.toFixed(2)), price };
        });

        const { items: processedItems, total_value: subtotal } = calculateTotal(payload);

        // Add Manual Items
        const manualBudgetItems = (room.manual_items || []).map((mItem: any) => {
          const val = typeof mItem.value === 'string' ? parseFloat(mItem.value) || 0 : mItem.value;
          return {
            service_type: `manual_${mItem.id}`,
            manual_name: mItem.name,
            quantity: 1,
            unit_price: val,
            subtotal: val,
          };
        });

        const finalRoomItems = [...processedItems, ...manualBudgetItems];
        const finalRoomTotal = subtotal + manualBudgetItems.reduce((acc: number, curr: any) => acc + curr.subtotal, 0);

        calculatedRooms.push({
          room_id: room.id,
          items: finalRoomItems,
          total: finalRoomTotal
        });
        grandTotal += finalRoomTotal;

      } catch (e) {
        return; // Stop calculation on error
      }
    }

    setCalculationResult({
      rooms: calculatedRooms,
      grand_total: grandTotal
    });
  };

  const handleSaveOrUpdate = async () => {
    if (!calculationResult || !clientName) { alert('Informe o nome do cliente para salvar.'); return; }

    setSaving(true);

    // Flat items list for DB, attached with room_id
    const allItems = calculationResult.rooms.flatMap((r: any) =>
      r.items.map((item: any) => ({
        ...item,
        room_id: r.room_id
      }))
    );

    const budgetData = {
      client_name: clientName,
      client_phone: clientPhone,
      notes: notes,
      width: 0, // Deprecated
      depth: 0, // Deprecated
      height: 0, // Deprecated
      rooms: rooms,
      selected_services: [], // Deprecated flat list
      items: allItems,
      total_value: calculationResult.grand_total,
      status: 'pendente' as const
    };

    if (editingId) {
      await budgetService.deleteBudget(editingId);
      const newBudget = await budgetService.createBudget(user.id, budgetData);
      if (newBudget) {
        alert('Orçamento atualizado com sucesso!');
        setEditingId(newBudget.id);
      } else {
        alert('Erro ao atualizar orçamento.');
      }
    } else {
      const newBudget = await budgetService.createBudget(user.id, budgetData);
      if (newBudget) {
        alert('Orçamento salvo no histórico!');
        setEditingId(newBudget.id);
      } else {
        alert('Erro ao salvar orçamento.');
      }
    }
    setSaving(false);
  };

  const generateBudgetText = () => {
    if (!calculationResult) return '';
    let text = `*Orçamento Gesso Milenio*\n\n`;
    text += `Cliente: ${clientName}\n`;
    if (notes) text += `Obs: ${notes}\n`;

    calculationResult.rooms.forEach((r: any) => {
      const roomCfg = rooms.find((Rm: Room) => Rm.id === r.room_id);
      const roomName = roomCfg?.name || 'Cômodo';
      const m = roomCfg ? getRoomMetrics(roomCfg) : { area: 0, perimeter: 0, wallArea: 0, h: 0 };
      text += `\n*${roomName}*\n`;
      if (m.area > 0) {
        text += `_${formatDim(roomCfg?.width)} × ${formatDim(roomCfg?.depth)} m — Total: ${formatArea(m.area)}_\n`;
      }
      r.items.forEach((item: any) => {
        const isManual = !!item.manual_name;
        const label = item.manual_name || getServiceLabel(item.service_type, profile);
        const unit = label.includes('(m²)') ? 'm²' : label.includes('(m)') ? 'm' : 'un';
        if (isManual) {
          text += `- ${label}: ${formatCurrency(item.subtotal)}\n`;
        } else {
          text += `- ${label.split(' (')[0]}: ${item.quantity}${unit} x ${formatCurrency(item.unit_price)} = ${formatCurrency(item.subtotal)}\n`;
        }
      });
      text += `Subtotal: ${formatCurrency(r.total)}\n`;
    });

    // Add Summary of materials
    text += `\n*RESUMO POR MATERIAL*\n`;
    const materialTotals = calculationResult.rooms.flatMap((r: any) => r.items).reduce((acc: any, item: any) => {
      const type = item.service_type;
      const key = item.manual_name ? `manual_${item.manual_name}` : type;
      if (!acc[key]) acc[key] = {
        quantity: 0,
        subtotal: 0,
        label: item.manual_name || getServiceLabel(type, profile),
        isManual: !!item.manual_name
      };
      acc[key].quantity += item.quantity;
      acc[key].subtotal += item.subtotal;
      return acc;
    }, {} as Record<string, { quantity: number; subtotal: number; label: string; isManual: boolean }>);

    (Object.entries(materialTotals) as [string, { quantity: number; subtotal: number; label: string; isManual: boolean }][]).forEach(([_, data]) => {
      const unit = data.label.includes('(m²)') ? 'm²' : data.label.includes('(m)') ? 'm' : 'un';
      if (data.isManual) {
        text += `- ${data.label}: ${formatCurrency(data.subtotal)}\n`;
      } else {
        text += `- ${data.label.split(' (')[0]}: ${data.quantity.toFixed(2)}${unit} = ${formatCurrency(data.subtotal)}\n`;
      }
    });

    const totalAreaM = rooms.reduce((acc, r) => acc + getRoomMetrics(r).area, 0);
    if (totalAreaM > 0) {
      text += `\n*METRAGEM TOTAL: ${formatArea(totalAreaM)}*\n`;
    }

    text += `\n*VALOR TOTAL: ${formatCurrency(calculationResult.grand_total)}*`;
    text += `\n\n_Gerado por GM_`;
    return text;
  };

  const copyToClipboard = async () => {
    const text = generateBudgetText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { alert('Erro ao copiar.'); }
  };

  const exportToPDF = async () => {
    if (!calculationResult || !profile) return;
    const { jsPDF } = (window as any).jspdf;
    const doc = new jsPDF();

    // Header logic (reuse existante logic mostly)
    doc.setFontSize(22);
    doc.setTextColor(37, 99, 235);
    const logoBase64 = await getLogoBase64();
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', 14, 10, 20, 20);
    }

    doc.setFontSize(22);
    doc.setTextColor(37, 99, 235);
    doc.text('Gesso Milenio', 105, 20, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(profile.company_name, 105, 27, { align: 'center' });

    doc.setFontSize(18);
    doc.setTextColor(30, 41, 59);
    doc.text(`Orçamento Profissional`, 14, 40);

    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text(`Cliente: ${clientName}`, 14, 50);
    doc.text(`Contato: ${clientPhone || 'N/A'}`, 14, 57);
    doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, 14, 64);

    let currentY = 80;

    calculationResult.rooms.forEach((roomUser: any, index: number) => {
      const roomConfig = rooms.find((r: Room) => r.id === roomUser.room_id);
      const roomName = roomConfig?.name || `Cômodo ${index + 1}`;
      const roomDims = roomConfig ? `${roomConfig.width}m x ${roomConfig.depth}m (Alt: ${roomConfig.height}m)` : '';
      const metrics = roomConfig ? getRoomMetrics(roomConfig) : { area: 0, perimeter: 0, wallArea: 0, h: 0 };

      // Add page if needed
      if (currentY > 250) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(30, 41, 59);
      doc.text(`${roomName} - ${roomDims}`, 14, currentY);
      currentY += 6;

      if (metrics.area > 0) {
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(`Total: ${formatArea(metrics.area)}`, 14, currentY);
        currentY += 4;
      }

      const tableRows = roomUser.items.map((item: any) => {
        const isManual = !!item.manual_name;
        const label = item.manual_name || getServiceLabel(item.service_type, profile);
        const unit = label.includes('(m²)') ? 'm²' : label.includes('(m)') ? 'm' : 'un';
        return [
          label.split(' (')[0],
          isManual ? 'Item Avulso' : `${item.quantity}${unit}`,
          isManual ? '-' : formatCurrency(item.unit_price),
          formatCurrency(item.subtotal)
        ];
      });

      (doc as any).autoTable({
        startY: currentY,
        head: [['Serviço', 'Quantidade', 'P. Unitário', 'Subtotal']],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [30, 41, 59] },
        foot: [['', '', 'Subtotal', formatCurrency(roomUser.total)]],
        footStyles: { fillColor: [100, 116, 139], textColor: [255, 255, 255], fontStyle: 'bold' },
        margin: { left: 14, right: 14 }
      });

      currentY = (doc as any).lastAutoTable.finalY + 15;
    });

    // Grand Total
    const finalY = currentY;
    doc.setFillColor(37, 99, 235);
    doc.rect(14, finalY, 180, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`TOTAL GERAL: ${formatCurrency(calculationResult.grand_total)}`, 105, finalY + 10, { align: 'center' });


    if (notes) {
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.setFont('helvetica', 'normal');
      doc.text(`Observações: ${notes}`, 14, finalY + 25, { maxWidth: 180 });
    }

    // Add material summary page
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(30, 41, 59);
    doc.text('Resumo por Material', 14, 20);

    // Metragem total consolidada (tabela por cômodo + total)
    const metricsRows = rooms.map((r: Room) => {
      const m = getRoomMetrics(r);
      return [r.name, `${formatDim(r.width)} × ${formatDim(r.depth)} m`, formatArea(m.area)];
    });
    const totalAreaPdf = rooms.reduce((acc, r) => acc + getRoomMetrics(r).area, 0);

    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text('Metragem por Cômodo', 14, 30);

    (doc as any).autoTable({
      startY: 34,
      head: [['Cômodo', 'Dimensões', 'Total']],
      body: metricsRows,
      theme: 'striped',
      headStyles: { fillColor: [30, 41, 59] },
      foot: [['', 'METRAGEM TOTAL', formatArea(totalAreaPdf)]],
      footStyles: { fillColor: [0, 91, 174], textColor: [255, 255, 255], fontStyle: 'bold' },
      margin: { left: 14, right: 14 }
    });

    const materialTotals = calculationResult.rooms.flatMap((r: any) => r.items).reduce((acc: any, item: any) => {
      const type = item.service_type;
      const key = item.manual_name ? `manual_${item.manual_name}` : type;
      if (!acc[key]) acc[key] = {
        quantity: 0,
        subtotal: 0,
        label: item.manual_name || getServiceLabel(type, profile),
        isManual: !!item.manual_name
      };
      acc[key].quantity += item.quantity;
      acc[key].subtotal += item.subtotal;
      return acc;
    }, {} as Record<string, { quantity: number; subtotal: number; label: string; isManual: boolean }>);

    const summaryRows = (Object.entries(materialTotals) as [string, { quantity: number; subtotal: number; label: string; isManual: boolean }][]).map(([_, data]) => {
      const unit = data.label.includes('(m²)') ? 'm²' : data.label.includes('(m)') ? 'm' : 'un';
      return [
        data.label.split(' (')[0],
        data.isManual ? 'Item Avulso' : `${data.quantity.toFixed(2)}${unit}`,
        formatCurrency(data.subtotal)
      ];
    });

    const matStartY = ((doc as any).lastAutoTable?.finalY || 30) + 12;
    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text('Total por Material', 14, matStartY - 4);

    (doc as any).autoTable({
      startY: matStartY,
      head: [['Material', 'Metragem Total', 'Valor Total']],
      body: summaryRows,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59] },
      foot: [['', 'TOTAL GERAL', formatCurrency(calculationResult.grand_total)]],
      footStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: 'bold' },
      margin: { left: 14, right: 14 }
    });

    const fileName = `Orcamento_GM_${clientName.replace(/\s+/g, '_')}.pdf`;

    try {
      const blob = doc.output('blob');
      const file = new File([blob], fileName, { type: 'application/pdf' });
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile && navigator.share && (navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Orçamento GM',
          text: `Orçamento para ${clientName}`
        });
      } else {
        doc.save(fileName);
      }
    } catch (error) {
      console.error('Error sharing PDF:', error);
      doc.save(fileName);
    }
  };

  const shareWhatsApp = () => {
    if (!clientPhone) { alert('Telefone não informado.'); return; }
    const text = generateBudgetText();
    if (!text) return;
    let phoneOnUrl = clientPhone.replace(/\D/g, '');
    if (phoneOnUrl.length >= 10 && phoneOnUrl.length <= 11) phoneOnUrl = '55' + phoneOnUrl;
    const url = `https://wa.me/${phoneOnUrl}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <div className="card p-8 text-center">
          <i className="fa-solid fa-user-gear text-4xl mb-4" style={{ color: 'var(--primary)' }}></i>
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--on-surface)' }}>Configure seu Perfil</h2>
          <p className="mb-6 text-sm" style={{ color: 'var(--on-surface-variant)' }}>Defina os preços dos serviços antes de usar a calculadora.</p>
          <button onClick={() => navigate('/perfil')} className="btn-primary">
            <i className="fa-solid fa-arrow-right"></i> Ir para Configurações
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-36">

      {/* ── Cabeçalho ── */}
      <div className="flex justify-between items-center mb-5">
        <div>
          <h1 className="text-xl font-black leading-tight" style={{ color: 'var(--on-surface)', letterSpacing: '-0.01em' }}>
            Calculadora <span style={{ color: 'var(--primary)' }}>GM</span>
          </h1>
          <p className="text-xs font-semibold mt-0.5" style={{ color: 'var(--on-surface-muted)' }}>
            {editingId ? '✏️ Editando orçamento' : 'Novo orçamento'}
          </p>
        </div>
        <button
          onClick={() => navigate('/historico')}
          className="w-10 h-10 flex items-center justify-center rounded-2xl transition-all"
          style={{ background: 'var(--surface-container-low)', color: 'var(--on-surface-variant)' }}
          title="Ver Histórico"
        >
          <i className="fa-solid fa-clock-rotate-left"></i>
        </button>
      </div>

      {/* ── Dados do Cliente ── */}
      <div className="card p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'var(--primary-container)' }}>
            <i className="fa-solid fa-user text-xs" style={{ color: 'var(--primary)' }}></i>
          </div>
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--primary)' }}>Informações do Cliente</span>
        </div>
        <div className="space-y-3">
          <input
            type="text"
            className="w-full px-4 py-3.5 rounded-2xl text-sm font-semibold outline-none transition-all"
            style={{ background: 'var(--surface-container-low)', color: 'var(--on-surface)', border: '1px solid rgba(169,174,177,0.15)' }}
            placeholder="Nome do Cliente"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = 'var(--primary)'; e.target.style.boxShadow = '0 0 0 3px rgba(0,91,174,0.1)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(169,174,177,0.15)'; e.target.style.boxShadow = 'none'; }}
          />
          <input
            type="tel"
            className="w-full px-4 py-3.5 rounded-2xl text-sm font-semibold outline-none transition-all"
            style={{ background: 'var(--surface-container-low)', color: 'var(--on-surface)', border: '1px solid rgba(169,174,177,0.15)' }}
            placeholder="WhatsApp (ex: 11999999999)"
            value={clientPhone}
            onChange={(e) => setClientPhone(e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = 'var(--primary)'; e.target.style.boxShadow = '0 0 0 3px rgba(0,91,174,0.1)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(169,174,177,0.15)'; e.target.style.boxShadow = 'none'; }}
          />
          <textarea
            className="w-full px-4 py-3.5 rounded-2xl text-sm font-medium outline-none transition-all resize-none h-20"
            style={{ background: 'var(--surface-container-low)', color: 'var(--on-surface)', border: '1px solid rgba(169,174,177,0.15)' }}
            placeholder="Observações do cliente..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = 'var(--primary)'; e.target.style.boxShadow = '0 0 0 3px rgba(0,91,174,0.1)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(169,174,177,0.15)'; e.target.style.boxShadow = 'none'; }}
          />
        </div>
      </div>

      {/* ── Abas de Cômodos ── */}
      <div className="flex items-center gap-2 mb-4 -mx-4 px-4 overflow-x-auto no-scrollbar py-1">
        {rooms.map((room, index) => (
          <div key={room.id} className="flex-shrink-0 flex items-center rounded-full overflow-hidden transition-all" style={activeRoomId === room.id
            ? { background: 'var(--primary)', boxShadow: '0 4px 12px rgba(0,91,174,0.3)' }
            : { background: 'var(--surface-container-lowest)', boxShadow: 'var(--shadow-card)' }
          }>
            <button
              onClick={() => setActiveRoomId(room.id)}
              className="h-10 px-4 flex items-center gap-2 text-xs font-bold whitespace-nowrap"
              style={{ color: activeRoomId === room.id ? 'white' : 'var(--on-surface-variant)' }}
            >
              <i className="fa-solid fa-layer-group text-[10px]"></i>
              {room.name || `Cômodo ${index + 1}`}
            </button>
            {rooms.length > 1 && (
              <button
                onClick={(e) => removeRoom(room.id, e)}
                className="h-10 px-3 flex items-center"
                style={{ color: activeRoomId === room.id ? 'rgba(255,255,255,0.6)' : 'var(--on-surface-muted)', borderLeft: '1px solid rgba(169,174,177,0.15)' }}
              >
                <i className="fa-solid fa-xmark text-[10px]"></i>
              </button>
            )}
          </div>
        ))}
        <button
          onClick={addRoom}
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all"
          style={{ background: 'var(--surface-container-lowest)', color: 'var(--primary)', border: '2px dashed rgba(0,91,174,0.25)' }}
          title="Adicionar cômodo"
        >
          <i className="fa-solid fa-plus text-xs"></i>
        </button>
      </div>

      {/* ── Editor do Cômodo Ativo ── */}
      <div className="card p-5 mb-4">
        {/* Nome do cômodo */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center gap-2 flex-1" style={{ background: 'var(--surface-container-low)', borderRadius: '1rem', padding: '0.5rem 0.875rem' }}>
            <i className="fa-solid fa-pen-nib text-xs" style={{ color: 'var(--primary)' }}></i>
            <input
              type="text"
              value={activeRoom.name}
              onChange={(e) => updateRoom(activeRoomId, { name: e.target.value })}
              className="flex-1 text-sm font-bold outline-none bg-transparent"
              style={{ color: 'var(--on-surface)' }}
              placeholder="Nome do cômodo (ex: Sala)"
            />
          </div>
        </div>

        {/* Dimensões */}
        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--on-surface-muted)' }}>Dimensões</p>
        <div className="grid grid-cols-3 gap-2.5 mb-6">
          {[
            { label: 'Largura', key: 'width' as const },
            { label: 'Comprimento', key: 'depth' as const },
            { label: 'Altura', key: 'height' as const },
          ].map((field) => (
            <div key={field.key} className="rounded-2xl p-3" style={{ background: 'var(--surface-container-low)' }}>
              <label className="text-[10px] font-bold uppercase tracking-widest block mb-1.5" style={{ color: 'var(--on-surface-muted)' }}>{field.label}</label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-full pr-5 text-base font-black outline-none bg-transparent"
                  style={{ color: 'var(--on-surface)' }}
                  placeholder="0,00"
                  value={(() => {
                    const raw = String(activeRoom[field.key] ?? '');
                    if (raw === '') return '';
                    const num = parseFloat(raw.replace(',', '.'));
                    if (isNaN(num)) return '';
                    return num.toFixed(2).replace('.', ',');
                  })()}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    if (digits === '') {
                      updateRoom(activeRoomId, { [field.key]: '' });
                      return;
                    }
                    const num = parseInt(digits, 10) / 100;
                    updateRoom(activeRoomId, { [field.key]: num.toFixed(2) });
                  }}
                />
                <span className="absolute right-0 top-1/2 -translate-y-1/2 text-xs font-bold" style={{ color: 'var(--primary)' }}>m</span>
              </div>
            </div>
          ))}
        </div>

        {/* Serviços Padrão */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <i className="fa-solid fa-screwdriver-wrench text-xs" style={{ color: 'var(--primary)' }}></i>
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--on-surface-muted)' }}>Serviços Padrão GM</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(Object.keys(SERVICE_LABELS) as ServiceType[]).map((service) => {
              const active = activeRoom.selected_services.includes(service);
              return (
                <label
                  key={service}
                  className="flex items-center gap-3 rounded-2xl cursor-pointer select-none transition-all"
                  style={{
                    padding: '0.75rem 1rem',
                    background: active ? 'var(--primary)' : 'var(--surface-container-low)',
                    boxShadow: active ? '0 4px 12px rgba(0,91,174,0.2)' : 'none',
                    minHeight: '48px',
                  }}
                >
                  <input type="checkbox" className="hidden" checked={active} onChange={() => toggleService(service)} />
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: active ? 'rgba(255,255,255,0.2)' : 'rgba(0,91,174,0.1)' }}>
                    <i className={`fa-solid ${active ? 'fa-check' : 'fa-plus'} text-[9px]`} style={{ color: active ? 'white' : 'var(--primary)' }}></i>
                  </div>
                  <span className="text-xs font-bold leading-tight" style={{ color: active ? 'white' : 'var(--on-surface)' }}>
                    {SERVICE_LABELS[service]}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Serviços Extras */}
        {profile.custom_services && profile.custom_services.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="fa-solid fa-star text-xs" style={{ color: 'var(--secondary)', filter: 'drop-shadow(0 0 4px rgba(163,250,0,0.5))' }}></i>
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--on-surface-muted)' }}>Seus Serviços Extras</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {profile.custom_services.map((service) => {
                const active = activeRoom.selected_services.includes(service.id);
                return (
                  <label
                    key={service.id}
                    className="flex items-center gap-3 rounded-2xl cursor-pointer select-none transition-all"
                    style={{
                      padding: '0.75rem 1rem',
                      background: active ? 'var(--primary)' : 'var(--surface-container-low)',
                      boxShadow: active ? '0 4px 12px rgba(0,91,174,0.2)' : 'none',
                      minHeight: '48px',
                    }}
                  >
                    <input type="checkbox" className="hidden" checked={active} onChange={() => toggleService(service.id)} />
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: active ? 'rgba(255,255,255,0.2)' : 'rgba(0,91,174,0.1)' }}>
                      <i className={`fa-solid ${active ? 'fa-check' : 'fa-plus'} text-[9px]`} style={{ color: active ? 'white' : 'var(--primary)' }}></i>
                    </div>
                    <span className="text-xs font-bold leading-tight" style={{ color: active ? 'white' : 'var(--on-surface)' }}>
                      {service.name}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Itens Avulsos */}
        <div style={{ borderTop: '1px solid rgba(169,174,177,0.12)', paddingTop: '1.25rem' }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--on-surface-muted)' }}>Itens Avulsos</span>
            <button
              onClick={addManualItem}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all"
              style={{ background: 'var(--primary-container)', color: 'var(--primary)' }}
            >
              <i className="fa-solid fa-plus text-[10px]"></i> Novo Item
            </button>
          </div>

          <div className="space-y-2">
            {activeRoom.manual_items?.map((item) => (
              <div key={item.id} className="flex gap-2 items-center p-3 rounded-2xl animate-fade-in" style={{ background: 'var(--surface-container-low)' }}>
                <textarea
                  placeholder="Ex: Limpeza, Frete..."
                  className="flex-1 text-sm font-medium outline-none bg-transparent resize-none"
                  style={{ color: 'var(--on-surface)', minHeight: '40px' }}
                  value={item.name}
                  onChange={(e: any) => updateManualItem(item.id, 'name', e.target.value)}
                />
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold" style={{ color: 'var(--on-surface-muted)' }}>R$</span>
                    <input
                      type="text"
                      className="w-24 pl-8 pr-3 py-2.5 rounded-xl text-sm font-black outline-none"
                      style={{ background: 'var(--surface-container-lowest)', color: 'var(--on-surface)' }}
                      placeholder="0,00"
                      value={item.value || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(',', '.');
                        if (val === '' || /^\d*\.?\d*$/.test(val) || val.includes(',')) {
                          updateManualItem(item.id, 'value', e.target.value);
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseBRLNumber(e.target.value);
                        if (val > 0) updateManualItem(item.id, 'value', formatBRLNumber(val));
                      }}
                    />
                  </div>
                  <button
                    onClick={() => removeManualItem(item.id)}
                    className="w-9 h-9 flex items-center justify-center rounded-xl transition-all"
                    style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}
                  >
                    <i className="fa-solid fa-trash-can text-xs"></i>
                  </button>
                </div>
              </div>
            ))}
            {(!activeRoom.manual_items || activeRoom.manual_items.length === 0) && (
              <div className="text-center py-5 rounded-2xl" style={{ background: 'var(--surface-container-low)' }}>
                <i className="fa-solid fa-clipboard-list block mb-1.5 text-xl" style={{ color: 'var(--on-surface-muted)' }}></i>
                <span className="text-xs font-medium" style={{ color: 'var(--on-surface-muted)' }}>Sem itens avulsos</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Botão CTA fixo no fundo (mobile) ── */}
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 px-4 pb-4 md:pb-6 z-40 pointer-events-none">
        <div className="max-w-2xl mx-auto pointer-events-auto">
          <button
            onClick={handleCalculate}
            className="w-full h-14 font-black text-sm uppercase tracking-widest flex items-center justify-center gap-3 transition-all active:scale-[0.98] rounded-full"
            style={{
              background: 'linear-gradient(135deg, #a3fa00 0%, #7dd100 100%)',
              color: '#1f3300',
              boxShadow: '0 8px 24px rgba(163,250,0,0.3)',
            }}
          >
            <i className="fa-solid fa-wand-magic-sparkles"></i>
            Gerar Orçamento Total
            <i className="fa-solid fa-chevron-right text-xs opacity-60"></i>
          </button>
        </div>
      </div>

      {/* ── Resultados ── */}
      {calculationResult && (
        <div className="space-y-4 animate-fade-in">
          {/* Card de resultado */}
          <div className="card overflow-hidden">
            {/* Header do resultado */}
            <div className="p-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dim) 100%)' }}>
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10" style={{ background: 'var(--secondary)', transform: 'translate(30%, -30%)' }}></div>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Total Geral</p>
              <span className="text-4xl font-black text-white" style={{ letterSpacing: '-0.02em' }}>
                {formatCurrency(calculationResult.grand_total)}
              </span>
            </div>

            {/* Por cômodo */}
            <div className="p-5 space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--on-surface-muted)' }}>Por Cômodo</p>
              {calculationResult.rooms.map((roomResult: any, idx: number) => {
                const roomCfg = rooms.find((r: Room) => r.id === roomResult.room_id);
                const roomName = roomCfg?.name || 'Cômodo';
                const m = roomCfg ? getRoomMetrics(roomCfg) : { area: 0, perimeter: 0, wallArea: 0, h: 0 };
                return (
                  <div key={idx} className="rounded-2xl p-4" style={{ background: 'var(--surface-container-low)' }}>
                    {/* Cabeçalho do cômodo */}
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-bold block truncate" style={{ color: 'var(--on-surface)' }}>{roomName}</span>
                        {m.area > 0 && (
                          <span className="text-[11px] font-medium" style={{ color: 'var(--on-surface-muted)' }}>
                            {formatDim(roomCfg?.width)} × {formatDim(roomCfg?.depth)} m · <strong style={{ color: 'var(--on-surface-variant)' }}>{formatArea(m.area)}</strong>
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-black whitespace-nowrap" style={{ color: 'var(--primary)' }}>{formatCurrency(roomResult.total)}</span>
                    </div>

                    {/* Serviços do cômodo */}
                    {roomResult.items.length > 0 && (
                      <div className="mt-3 pt-3 space-y-1.5" style={{ borderTop: '1px solid rgba(169,174,177,0.15)' }}>
                        {roomResult.items.map((item: any, iIdx: number) => {
                          const isManual = !!item.manual_name;
                          const label = item.manual_name || getServiceLabel(item.service_type, profile);
                          const unit = label.includes('(m²)') ? ' m²' : label.includes('(m)') ? ' m' : ' un';
                          return (
                            <div key={iIdx} className="flex justify-between items-center text-[11px] gap-2">
                              <span className="font-medium truncate" style={{ color: 'var(--on-surface)' }}>{label.split(' (')[0]}</span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {!isManual && (
                                  <span className="font-bold tabular-nums" style={{ color: 'var(--on-surface-muted)' }}>
                                    {item.quantity.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{unit}
                                  </span>
                                )}
                                <span className="font-black tabular-nums" style={{ color: 'var(--on-surface)' }}>{formatCurrency(item.subtotal)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Metragem Total Consolidada */}
              {(() => {
                const totalArea = rooms.reduce((acc, r) => acc + getRoomMetrics(r).area, 0);
                if (totalArea === 0) return null;
                return (
                  <div className="mt-2 flex justify-between items-center rounded-2xl px-4 py-3" style={{ background: 'linear-gradient(135deg, rgba(0,91,174,0.08) 0%, rgba(95,161,255,0.06) 100%)' }}>
                    <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: 'var(--primary)' }}>
                      <i className="fa-solid fa-ruler-combined mr-1.5"></i>Metragem Total
                    </span>
                    <span className="text-base font-black" style={{ color: 'var(--on-surface)' }}>{formatArea(totalArea)}</span>
                  </div>
                );
              })()}
            </div>

            {/* Consolidado por serviço */}
            <div className="mx-5 mb-5 rounded-2xl overflow-hidden" style={{ background: 'var(--surface-container-low)' }}>
              <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(169,174,177,0.12)' }}>
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--on-surface-muted)' }}>Consolidado por Serviço</span>
              </div>
              <div className="p-4 space-y-3">
                {Object.entries(
                  calculationResult.rooms.flatMap((r: any) => r.items).reduce((acc: any, item: any) => {
                    const type = item.service_type;
                    const key = item.manual_name ? `manual_${item.manual_name}` : type;
                    if (!acc[key]) acc[key] = { quantity: 0, subtotal: 0, label: item.manual_name || getServiceLabel(type, profile), isManual: !!item.manual_name };
                    acc[key].quantity += item.quantity;
                    acc[key].subtotal += item.subtotal;
                    return acc;
                  }, {} as Record<string, { quantity: number; subtotal: number; label: string; isManual: boolean }>)
                ).map(([key, data], idx) => {
                  const typedData = data as { quantity: number; subtotal: number; label: string; isManual: boolean };
                  const unit = typedData.label.includes('(m²)') ? ' m²' : typedData.label.includes('(m)') ? ' m' : ' un';
                  return (
                    <div key={idx} className="flex justify-between items-center">
                      <div>
                        <span className="text-sm font-semibold block" style={{ color: 'var(--on-surface)' }}>{typedData.label.split(' (')[0]}</span>
                        <span className="text-xs" style={{ color: 'var(--on-surface-muted)' }}>
                          {typedData.isManual ? 'Item avulso' : `${typedData.quantity.toFixed(2)}${unit}`}
                        </span>
                      </div>
                      <span className="text-sm font-black" style={{ color: 'var(--primary)' }}>{formatCurrency(typedData.subtotal)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ações */}
            <div className="grid grid-cols-2 gap-3 p-5 pt-0">
              <button
                onClick={handleSaveOrUpdate}
                disabled={saving}
                className="col-span-2 h-12 font-bold rounded-full flex items-center justify-center gap-2 transition-all text-sm"
                style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dim) 100%)', color: 'white', boxShadow: 'var(--shadow-primary)', opacity: saving ? 0.6 : 1 }}
              >
                {saving ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  : <><i className="fa-solid fa-floppy-disk"></i> {editingId ? 'Atualizar Orçamento' : 'Salvar Orçamento'}</>}
              </button>

              <button
                onClick={copyToClipboard}
                className="h-12 font-bold rounded-full flex items-center justify-center gap-2 text-sm transition-all"
                style={copied
                  ? { background: 'var(--primary)', color: 'white' }
                  : { background: 'var(--surface-container-low)', color: 'var(--on-surface)' }
                }
              >
                <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                {copied ? 'Copiado!' : 'Copiar'}
              </button>

              <button
                onClick={exportToPDF}
                className="h-12 font-bold rounded-full flex items-center justify-center gap-2 text-sm transition-all"
                style={{ background: 'var(--surface-container-low)', color: 'var(--on-surface)' }}
              >
                <i className="fa-solid fa-file-pdf"></i> PDF
              </button>

              <button
                onClick={shareWhatsApp}
                className="col-span-2 h-12 font-bold rounded-full flex items-center justify-center gap-2 text-sm transition-all"
                style={{ background: '#25D366', color: 'white', boxShadow: '0 4px 12px rgba(37,211,102,0.25)' }}
              >
                <i className="fa-brands fa-whatsapp text-base"></i> Compartilhar no WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calculator;
